import axios from 'axios'
import { Auth } from 'aws-amplify'

const client = axios.create({ baseURL: process.env.VUE_APP_ROOT_API, timeout: 15000 })
const SESSION = 'sb-growth-session'
const ATTRIBUTION = 'sb-growth-attribution'
const tag = value => (typeof value === 'string' && /^[a-z0-9][a-z0-9_-]{0,63}$/i.test(value) ? value.toLowerCase() : '')
let memory
let attributionMemory
let queue = Promise.resolve()
let identified = ''
let identifiedAt = 0
function id() {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] % 16) + 64
  bytes[8] = (bytes[8] % 64) + 128
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
function read(storage, key) { try { return JSON.parse(window[storage].getItem(key)) } catch (_) { return null } }
function write(storage, key, value) { try { window[storage].setItem(key, JSON.stringify(value)) } catch (_) { /* Optional browser storage. */ } }
function pageFor(path) {
  if (/^\/user\/[^/]+\/pictures/.test(path)) return '/public/pictures'
  if (/^\/user\/[^/]+\/biography/.test(path)) return '/public/biography'
  if (path.startsWith('/user/')) return '/public/profile'
  if (path.startsWith('/pages/')) return '/pages'
  if (['/landingpage', '/biographies', '/auth', '/my-pictures', '/my-biography', '/my-world', '/my-diary', '/billing', '/referrals'].includes(path)) return path

  return '/other'
}
async function record(route) {
  if (route.path.startsWith('/admin') || navigator.doNotTrack === '1' || navigator.globalPrivacyControl === true || !window.crypto) return
  let auth
  try { auth = await Auth.currentSession() } catch (_) { /* Anonymous navigation is recorded without account identity. */ }
  if (auth && (auth.getAccessToken().payload['cognito:groups'] || []).includes('affiliate-admins')) return
  const now = Date.now()
  let session = memory || read('sessionStorage', SESSION)
  const campaign = tag(route.query.utm_campaign)

  // A tagged visit starts a new session if the campaign changes.
  if (!session || now - session.lastSeen > 30 * 60000 || now - session.started > 86400000 || (campaign && campaign !== session.campaign)) {
    session = {
      id: id(), campaign, source: tag(route.query.utm_source), medium: tag(route.query.utm_medium), started: now, lastSeen: now,
    }
  }
  session.lastSeen = now
  memory = session
  write('sessionStorage', SESSION, session)
  let referrerHost = ''
  try {
    const host = new URL(document.referrer).hostname
    if (![window.location.hostname, 'beta.selfbiography.com', 'selfbiography.com', 'www.selfbiography.com'].includes(host)) referrerHost = host
  } catch (_) { /* Referrer is absent on direct visits. */ }
  let device = 'desktop'
  if (/iPad|Tablet/i.test(navigator.userAgent)) device = 'tablet'
  else if (/Mobile|iPhone|Android/i.test(navigator.userAgent)) device = 'mobile'
  const recorded = await client.post('/analytics/visit', {
    sessionId: session.id,
    eventId: id(),
    page: pageFor(route.path),
    campaign: session.campaign,
    source: session.source,
    medium: session.medium,
    referrerHost,
    device,
  })
  let attribution = attributionMemory || read('localStorage', ATTRIBUTION)
  if (attribution && attribution.expires < now) attribution = null
  if (!attribution && recorded.data.campaign) {
    attribution = { id: session.id, expires: now + 30 * 86400000 }
    attributionMemory = attribution
    write('localStorage', ATTRIBUTION, attribution)
  }
  if (auth && (identified !== auth.getAccessToken().payload.sub || now - identifiedAt > 5 * 60000)) {
    await client.post('/analytics/identify', { sessionId: session.id, attributionId: attribution && attribution.id }, { headers: { Authorization: auth.getAccessToken().getJwtToken() } })
    identified = auth.getAccessToken().payload.sub
    identifiedAt = now
  }
}
export function trackNavigation(route) {
  queue = queue.catch(() => {}).then(() => record(route)).catch(() => {})
}
async function adminRequest(method, path, data, params) {
  const token = (await Auth.currentSession()).getAccessToken().getJwtToken()

  return (await client.request({
    method, url: path, data, params, headers: { Authorization: token }, timeout: 30000,
  })).data
}
export const growth = {
  overview: params => adminRequest('get', '/admin/growth', undefined, params),
  createCampaign: data => adminRequest('post', '/admin/growth/campaigns', data),
  removeCampaign: slug => adminRequest('post', `/admin/growth/campaigns/${encodeURIComponent(slug)}/remove`),
  resolveCampaign: async code => (await client.get(`/analytics/campaigns/${encodeURIComponent(code)}`)).data,
}
